import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { usePricing } from '../store/pricing';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { useCookieConsent, COOKIE_CATEGORIES } from '../context/CookieContext';
import { 
  addEcommerceItem, 
  trackEcommerceCartUpdate, 
  trackEvent 
} from '../services/matomo';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { Slider } from '../components/ui/slider';
import { Switch } from '../components/ui/switch';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import { 
  CreditCard, 
  Loader2, 
  Globe, 
  CheckCircle2, 
  XCircle, 
  AlertCircle,
  Server,
  Shield,
  ShoppingCart,
  FileText,
  ArrowRight,
  ArrowLeft,
  Copy,
  HelpCircle,
  Check,
  Rocket,
  Zap,
  Sparkles,
  RotateCcw,
  Tag,
  Receipt,
  Scale,
  ExternalLink,
  RefreshCw,
  Calendar
} from 'lucide-react';
import { Checkbox } from '../components/ui/checkbox';

// Helper component for selected domain preview (new version)
const SelectedDomainPreviewNew = ({ selectedDomain, domainInfo }) => {
  if (!domainInfo) return null;
  
  const priceDisplay = domainInfo.price_registration 
    ? domainInfo.price_registration.toFixed(2) 
    : '0.00';
  
  return (
    <div className="rounded-lg bg-primary/5 border border-primary/20 p-4 space-y-2">
      <p className="text-sm text-muted-foreground">Deine Cloud wird erreichbar sein unter:</p>
      <p className="font-mono text-lg font-medium text-primary">
        https://{selectedDomain}
      </p>
      <div className="flex items-center gap-2 pt-2 border-t border-primary/10">
        <span className="text-sm text-muted-foreground">Jährliche Domain-Kosten:</span>
        <span className="font-semibold">{priceDisplay} €</span>
      </div>
    </div>
  );
};

// Domain Option Button Component (for full domain search)
const DomainOptionButton = ({ result, isSearched, isSelected, onSelect }) => {
  const buttonClass = result.available
    ? (isSelected ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50 cursor-pointer')
    : 'border-muted bg-muted/30 opacity-60 cursor-not-allowed';
  
  const priceText = result.price_registration ? result.price_registration.toFixed(2) : '0.00';
  const testId = 'domain-option-' + result.domain.replace(/\./g, '-');
  
  // Check if this is our recommendation (.cloud TLD)
  const isRecommended = result.tld === '.cloud' && result.available;
  const hasRecommendationMessage = result.message && result.message.indexOf('Empfehlung') !== -1;
  
  return (
    <button
      onClick={() => result.available && onSelect(result)}
      disabled={!result.available}
      className={'flex items-center justify-between p-3 rounded-lg border-2 transition-all ' + buttonClass + (isRecommended ? ' ring-2 ring-primary/30' : '')}
      data-testid={testId}
    >
      <div className="flex items-center gap-3">
        {result.available && isSelected && <CheckCircle2 className="h-5 w-5 text-primary" />}
        {result.available && !isSelected && <div className="h-5 w-5 rounded-full border-2 border-muted-foreground/30" />}
        {!result.available && <XCircle className="h-5 w-5 text-muted-foreground" />}
        <span className={result.available ? 'font-mono font-medium' : 'font-mono font-medium text-muted-foreground'}>
          {result.domain}
        </span>
        {isSearched && <Badge variant="secondary" className="text-xs">Deine Suche</Badge>}
        {(isRecommended || hasRecommendationMessage) && <Badge className="text-xs bg-gradient-to-r from-primary to-primary/80">🌟 Empfehlung</Badge>}
        {result.premium && <Badge variant="outline" className="text-xs">Premium</Badge>}
      </div>
      <div className="text-right">
        {result.available && (
          <div>
            <span className="font-semibold text-primary">{priceText} €</span>
            <span className="text-xs text-muted-foreground">/Jahr</span>
          </div>
        )}
        {!result.available && <span className="text-sm text-muted-foreground">{result.error || 'Vergeben'}</span>}
      </div>
    </button>
  );
};

// Demo Mode Notice Component
const DemoModeNotice = ({ results }) => {
  if (!results || results.length === 0) return null;
  const firstResult = results[0];
  if (!firstResult.message) return null;
  if (firstResult.message.indexOf('DEMO') === -1) return null;
  
  return (
    <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30">
      <AlertCircle className="h-4 w-4 text-amber-500 mt-0.5" />
      <p className="text-xs text-amber-600 dark:text-amber-400">
        Demo-Modus: Die tatsächlichen Preise und Verfügbarkeiten werden über die InternetX API abgerufen.
      </p>
    </div>
  );
};

// Domain Results List Component (new - shows searched domain first, then alternatives)
const DomainResultsList = ({ results, searchedDomain, selectedDomain, onSelect }) => {
  const items = [];
  for (let i = 0; i < results.length; i++) {
    const result = results[i];
    const isSearched = result.domain === searchedDomain;
    const isSelected = result.domain === selectedDomain;
    items.push(
      <DomainOptionButton
        key={result.domain}
        result={result}
        isSearched={isSearched}
        isSelected={isSelected}
        onSelect={onSelect}
      />
    );
  }
  return <div className="grid gap-2">{items}</div>;
};

export const CheckoutPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, isAuthenticated, token } = useAuth();
  const { hasConsent } = useCookieConsent();
  const { addCloud, addDomain, setIsCartOpen, cart } = useCart();
  const { 
    storage_gb, 
    collabora_users, 
    antivirus, 
    hp_backend, 
    breakdown,
    setStorageGb,
    setCollaboraUsers,
    setAntivirus,
    setHpBackend,
    calculatePrice,
    prices,
    fetchPrices
  } = usePricing();
  
  // Fetch prices on mount
  useEffect(() => {
    fetchPrices();
  }, [fetchPrices]);
  
  const [activeTab, setActiveTabState] = useState('konfiguration');
  const [loading, setLoading] = useState(false);
  
  // Wrapper for setActiveTab that scrolls to top
  const setActiveTab = (tab) => {
    setActiveTabState(tab);
    // Smooth scroll to top when changing tabs
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  // Resume order state
  const [resumeOrderId, setResumeOrderId] = useState(null);
  const [resumeData, setResumeData] = useState(null);
  
  // Coupon/Gutschein state
  const [couponCode, setCouponCode] = useState('');
  const [couponLoading, setCouponLoading] = useState(false);
  const [appliedCoupon, setAppliedCoupon] = useState(null); // { code, discount_type, discount_value, discount_amount, applies_to_domain, message }
  
  // AGB & SLA acceptance
  const [agbAccepted, setAgbAccepted] = useState(false);
  const [slaAccepted, setSlaAccepted] = useState(false);

  // Domain option state: 'byod', 'subdomain', 'purchase'
  const [domainOption, setDomainOption] = useState('byod');
  
  // Subdomain state
  const [subdomain, setSubdomain] = useState('');
  const [subdomainCheck, setSubdomainCheck] = useState({
    loading: false,
    checked: false,
    available: false,
    message: '',
    fullDomain: ''
  });

  // Domain purchase state - now expects full domain like "meinefirma.de"
  const [purchaseDomain, setPurchaseDomain] = useState('');
  const [domainPurchaseCheck, setDomainPurchaseCheck] = useState({
    loading: false,
    checked: false,
    searchedDomain: '',  // The domain user searched for
    results: []  // Results including alternatives
  });
  
  // Domain usage configuration (for purchased domains)
  // User can choose: main domain (ALIAS) or subdomain (CNAME)
  const [domainUsage, setDomainUsage] = useState({
    useSubdomain: false,  // false = use main domain, true = use subdomain
    subdomain: '',        // e.g., "cloud" for cloud.meinefirma.de
    recordType: 'ALIAS'   // ALIAS for main domain, CNAME for subdomain
  });

  // Form state
  const [formData, setFormData] = useState({
    email: '',
    password: '',  // Passwort für Account-Erstellung (nur wenn nicht eingeloggt)
    firstName: '',
    lastName: '',
    company: '',
    street: '',
    zipCode: '',
    city: '',
    country: 'DE',
    phone: '',  // Telefonnummer für Domain-Registrierung (WHOIS)
    domain: '',
    paymentMethod: 'stripe'
  });
  
  // Customer data loaded state (for logged-in users)
  const [existingCustomer, setExistingCustomer] = useState(null);
  const [loadingCustomer, setLoadingCustomer] = useState(false);

  const [domainCheck, setDomainCheck] = useState({
    loading: false,
    checked: false,
    isValid: false,
    message: '',
    instructions: null
  });

  // Function to add cloud config to cart
  const addCloudToCart = async () => {
    // Calculate price if not already calculated
    if (!breakdown) {
      await calculatePrice();
    }
    
    // Get current breakdown after calculation
    const currentBreakdown = breakdown || {};
    
    // Determine domain config
    let domainConfig = null;
    
    if (domainOption === 'subdomain') {
      domainConfig = {
        type: 'subdomain',
        name: `${subdomain}.helden.land`,
        price: 0
      };
    } else if (domainOption === 'purchase') {
      const selectedDomainInfo = getSelectedDomainInfo();
      const domainPrice = selectedDomainInfo?.price_registration || 0;
      domainConfig = {
        type: 'purchase',
        // Die ausgewählte Domain (formData.domain) ist maßgeblich – sie bestimmt auch den Preis.
        // purchaseDomain ist nur der rohe Sucheingabewert und kann eine andere TLD haben.
        name: formData.domain || purchaseDomain,
        price: domainPrice
      };
    } else if (domainOption === 'byod') {
      domainConfig = {
        type: 'byod',
        name: formData.byodDomain,
        price: 0
      };
    }

    // Calculate total monthly price manually if breakdown not available
    let monthlyPrice = currentBreakdown?.final_price || currentBreakdown?.total_monthly;
    if (!monthlyPrice && prices) {
      monthlyPrice = prices.base_price || 9.00;
      const extraStorage = Math.max(0, storage_gb - (prices.storage_base_gb || 250));
      const extraStorageBlocks = Math.ceil(extraStorage / (prices.storage_step_gb || 50));
      monthlyPrice += extraStorageBlocks * (prices.storage_extra_50gb || 1.18);
      monthlyPrice += collabora_users * (prices.collabora_per_user || 2.50);
      if (antivirus) monthlyPrice += prices.antivirus || 2.00;
      if (hp_backend) monthlyPrice += prices.hp_backend || 5.00;
    }

    const cloudConfig = {
      plan: { id: 'standard', name: 'Wolkenhelden Cloud' },
      storage: storage_gb,
      collabora_users: collabora_users,
      antivirus: antivirus,
      hp_backend: hp_backend,
      domain: domainConfig,
      pricing: {
        monthly: monthlyPrice || 9.00,
        setup: 0,
        domain_yearly: 0 // Domain is now separate item
      },
      coupon: appliedCoupon ? {
        code: appliedCoupon.code,
        discount_percent: appliedCoupon.discount_value
      } : null,
      // Store form data for later
      formData: {
        ...formData,
        subdomain,
        purchaseDomain,
        domainOption,
        domainUsage
      }
    };

    // Add cloud to cart
    addCloud(cloudConfig);
    
    // Domain ist bereits Teil der Cloud-Konfiguration (cloud.domain) -
    // NICHT nochmal als separates Domain-Item hinzufügen, sonst Doppelberechnung!
    
    toast.success('Cloud-Konfiguration zum Warenkorb hinzugefügt!');
    setIsCartOpen(true);
  };

  // Handle cancelled payment and resume order from URL params
  useEffect(() => {
    const cancelled = searchParams.get('cancelled');
    const resumeId = searchParams.get('resume');
    const resumeToken = searchParams.get('token');  // Token für Resume ohne Login
    
    if (cancelled === 'true') {
      toast.error('Zahlung wurde abgebrochen. Du kannst es jederzeit erneut versuchen.', {
        duration: 6000,
        icon: '🛒'
      });
      // Restore data from localStorage
      restoreCheckoutData();
      
      // Notify backend about cancellation if we have order info
      const orderId = searchParams.get('order_id');
      if (orderId) {
        notifyPaymentCancelled(orderId);
      }
      // Clear the cancelled param from URL
      searchParams.delete('cancelled');
      searchParams.delete('order_id');
      setSearchParams(searchParams, { replace: true });
    } else if (resumeToken) {
      // Resume with token (no login required)
      loadResumeOrderWithToken(resumeToken);
    } else if (resumeId) {
      // Legacy resume (requires login)
      setResumeOrderId(resumeId);
      loadResumeOrder(resumeId);
    } else {
      // Normal page load - restore any saved data
      restoreCheckoutData();
    }
  }, []);
  
  // Load existing customer data for logged-in users
  useEffect(() => {
    if (isAuthenticated && user?.id && token) {
      loadExistingCustomer();
    }
  }, [isAuthenticated, user?.id, token]);
  
  // Save checkout data to localStorage whenever it changes
  useEffect(() => {
    saveCheckoutData();
  }, [formData, domainOption, subdomain, purchaseDomain, domainUsage, storage_gb, collabora_users, antivirus, hp_backend, appliedCoupon]);
  
  // Save checkout data to localStorage
  const saveCheckoutData = () => {
    const checkoutData = {
      formData,
      domainOption,
      subdomain,
      purchaseDomain,
      domainUsage,
      config: { storage_gb, collabora_users, antivirus, hp_backend },
      appliedCoupon,
      savedAt: new Date().toISOString()
    };
    localStorage.setItem('wolkenhelden_checkout', JSON.stringify(checkoutData));
  };
  
  // Restore checkout data from localStorage
  const restoreCheckoutData = () => {
    try {
      const saved = localStorage.getItem('wolkenhelden_checkout');
      if (!saved) return;
      
      const data = JSON.parse(saved);
      
      // Check if data is not too old (24 hours)
      const savedAt = new Date(data.savedAt);
      const now = new Date();
      const hoursDiff = (now - savedAt) / (1000 * 60 * 60);
      if (hoursDiff > 24) {
        localStorage.removeItem('wolkenhelden_checkout');
        return;
      }
      
      // Restore form data (but not password)
      if (data.formData) {
        setFormData(prev => ({
          ...prev,
          ...data.formData,
          password: ''  // Don't restore password
        }));
      }
      
      // Restore domain settings
      if (data.domainOption) setDomainOption(data.domainOption);
      if (data.subdomain) setSubdomain(data.subdomain);
      if (data.purchaseDomain) setPurchaseDomain(data.purchaseDomain);
      if (data.domainUsage) setDomainUsage(data.domainUsage);
      
      // Restore config
      if (data.config) {
        if (data.config.storage_gb) setStorageGb(data.config.storage_gb);
        if (data.config.collabora_users !== undefined) setCollaboraUsers(data.config.collabora_users);
        if (data.config.antivirus !== undefined) setAntivirus(data.config.antivirus);
        if (data.config.hp_backend !== undefined) setHpBackend(data.config.hp_backend);
      }
      
      // Restore coupon
      if (data.appliedCoupon) setAppliedCoupon(data.appliedCoupon);
      
      console.log('[Checkout] Restored data from localStorage');
    } catch (error) {
      console.error('Failed to restore checkout data:', error);
    }
  };
  
  // Clear checkout data from localStorage (after successful order)
  const clearCheckoutData = () => {
    localStorage.removeItem('wolkenhelden_checkout');
  };
  
  // Load existing customer data for logged-in users
  const loadExistingCustomer = async () => {
    setLoadingCustomer(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${backendUrl}/api/user/customer`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const customer = await response.json();
        setExistingCustomer(customer);
        
        // Pre-fill form with existing data
        setFormData(prev => ({
          ...prev,
          email: user?.email || prev.email,
          firstName: customer.first_name || prev.firstName,
          lastName: customer.last_name || prev.lastName,
          company: customer.company || prev.company,
          street: customer.street || prev.street,
          zipCode: customer.zip_code || prev.zipCode,
          city: customer.city || prev.city,
          country: customer.country || prev.country
        }));
        
        console.log('[Checkout] Loaded existing customer data');
      } else if (response.status === 404) {
        // No existing customer - just use email
        setFormData(prev => ({
          ...prev,
          email: user?.email || prev.email
        }));
      }
    } catch (error) {
      console.error('Failed to load customer data:', error);
      // Still set email from user
      setFormData(prev => ({
        ...prev,
        email: user?.email || prev.email
      }));
    } finally {
      setLoadingCustomer(false);
    }
  };
  
  // Load order with resume token (no login required)
  const loadResumeOrderWithToken = async (token) => {
    setLoading(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${backendUrl}/api/orders/resume/${token}`);
      
      if (!response.ok) {
        if (response.status === 410) {
          toast.error('Dieser Link ist abgelaufen. Bitte starte eine neue Bestellung.');
        } else if (response.status === 404) {
          toast.error('Bestellung nicht gefunden.');
        } else {
          toast.error('Fehler beim Laden der Bestellung.');
        }
        // Clear token from URL
        searchParams.delete('token');
        setSearchParams(searchParams, { replace: true });
        return;
      }
      
      const data = await response.json();
      setResumeData(data);
      
      // Pre-fill form with existing data
      if (data.plan) {
        setStorageGb(data.plan.storage_gb || 250);
        setCollaboraUsers(data.plan.collabora_users || 0);
        setAntivirus(data.plan.antivirus || false);
        setHpBackend(data.plan.hp_backend || false);
        
        if (data.plan.custom_domain) {
          setFormData(prev => ({ ...prev, domain: data.plan.custom_domain }));
        }
      }
      
      if (data.customer) {
        setFormData(prev => ({
          ...prev,
          firstName: data.customer.first_name || '',
          lastName: data.customer.last_name || '',
          company: data.customer.company || '',
          email: data.customer.email || '',
          street: data.customer.street || '',
          zipCode: data.customer.zip_code || '',
          city: data.customer.city || ''
        }));
      }
      
      // Jump to payment step
      setActiveTab('zahlung');
      toast.success('Bestellung geladen! Du kannst jetzt die Zahlung abschließen.', {
        icon: '🔄'
      });
      
      // Clear token from URL
      searchParams.delete('token');
      setSearchParams(searchParams, { replace: true });
      
    } catch (error) {
      console.error('Failed to load resume order:', error);
      toast.error('Bestellung konnte nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };
  
  // Notify backend about cancelled payment
  const notifyPaymentCancelled = async (orderId) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      await fetch(`${backendUrl}/api/payments/cancelled/${orderId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
    } catch (error) {
      console.error('Failed to notify payment cancellation:', error);
    }
  };
  
  // Load order data for resuming
  const loadResumeOrder = async (orderId) => {
    if (!isAuthenticated || !token) {
      toast.info('Bitte melde dich an, um deine Bestellung fortzusetzen.');
      navigate('/login', { state: { from: { pathname: `/checkout?resume=${orderId}` } } });
      return;
    }
    
    try {
      setLoading(true);
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${backendUrl}/api/orders/${orderId}/resume`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.status === 410) {
        toast.error('Diese Bestellung ist leider abgelaufen. Bitte starte eine neue Bestellung.');
        searchParams.delete('resume');
        setSearchParams(searchParams, { replace: true });
        setResumeOrderId(null);
        return;
      }
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Bestellung konnte nicht geladen werden');
      }
      
      const data = await response.json();
      setResumeData(data);
      
      // Pre-fill form with existing data
      if (data.plan) {
        setStorageGb(data.plan.storage_gb || 250);
        setCollaboraUsers(data.plan.collabora_users || 0);
        setAntivirus(data.plan.antivirus || false);
        setHpBackend(data.plan.hp_backend || false);
        
        if (data.plan.custom_domain) {
          setFormData(prev => ({ ...prev, domain: data.plan.custom_domain }));
        }
      }
      
      if (data.customer) {
        setFormData(prev => ({
          ...prev,
          firstName: data.customer.first_name || '',
          lastName: data.customer.last_name || '',
          company: data.customer.company || '',
          email: data.customer.email || ''
        }));
      }
      
      // Jump to payment step
      setActiveTab('zahlung');
      toast.success('Bestellung geladen! Du kannst jetzt die Zahlung abschließen.', {
        icon: '🔄'
      });
      
    } catch (error) {
      console.error('Failed to load resume order:', error);
      toast.error(error.message || 'Bestellung konnte nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  // Calculate price on mount and when config changes
  useEffect(() => {
    calculatePrice();
  }, [storage_gb, collabora_users, antivirus, hp_backend, calculatePrice]);

  // We no longer need to load all TLDs - just check specific domains
  // Remove the loadTlds useEffect
  
  // Coupon validation function
  const validateCoupon = async () => {
    if (!couponCode.trim()) {
      toast.error('Bitte gib einen Gutscheincode ein');
      return;
    }
    
    setCouponLoading(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      // For validation, send the hosting cost (without domain) so backend can recalculate
      const hostingCost = breakdown?.total || 0;
      const selectedDomainInfo = getSelectedDomainInfo();
      const domainCost = (domainOption === 'purchase' && selectedDomainInfo?.price_registration) ? selectedDomainInfo.price_registration : 0;
      
      const response = await fetch(`${backendUrl}/api/coupons/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code: couponCode.trim(),
          order_value: hostingCost + domainCost  // Total order value for validation
        })
      });
      
      const data = await response.json();
      
      if (data.valid) {
        // Recalculate discount based on applies_to_domain
        let actualDiscount = data.discount_amount;
        if (data.applies_to_domain === false && domainCost > 0) {
          // Coupon doesn't apply to domain - recalculate based on hosting only
          if (data.discount_type === 'percent') {
            actualDiscount = hostingCost * (data.discount_value / 100);
          } else {
            actualDiscount = Math.min(data.discount_value, hostingCost);
          }
          actualDiscount = Math.round(actualDiscount * 100) / 100;
        }
        
        setAppliedCoupon({
          ...data,
          discount_amount: actualDiscount,
          original_discount_amount: data.discount_amount  // Keep original for reference
        });
        
        // Show appropriate message
        if (data.applies_to_domain === false && domainCost > 0) {
          toast.success(`${data.message} (Domain-Kosten ausgenommen)`, { icon: '🎉' });
        } else {
          toast.success(data.message, { icon: '🎉' });
        }
      } else {
        setAppliedCoupon(null);
        toast.error(data.message);
      }
    } catch (error) {
      console.error('Coupon validation error:', error);
      toast.error('Fehler bei der Gutschein-Prüfung');
    } finally {
      setCouponLoading(false);
    }
  };
  
  // Remove applied coupon
  const removeCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode('');
    toast.info('Gutschein entfernt');
  };
  
  // Recalculate coupon discount when domain changes
  useEffect(() => {
    if (appliedCoupon && appliedCoupon.applies_to_domain === false) {
      const hostingCost = breakdown?.total || 0;
      const selectedDomainInfo = getSelectedDomainInfo();
      const domainCost = (domainOption === 'purchase' && selectedDomainInfo?.price_registration) ? selectedDomainInfo.price_registration : 0;
      
      let actualDiscount;
      if (appliedCoupon.discount_type === 'percent') {
        actualDiscount = hostingCost * (appliedCoupon.discount_value / 100);
      } else {
        actualDiscount = Math.min(appliedCoupon.discount_value, hostingCost);
      }
      actualDiscount = Math.round(actualDiscount * 100) / 100;
      
      if (actualDiscount !== appliedCoupon.discount_amount) {
        setAppliedCoupon(prev => ({
          ...prev,
          discount_amount: actualDiscount
        }));
      }
    }
  }, [domainOption, formData.domain, breakdown?.total, appliedCoupon, domainPurchaseCheck.results]);
  
  // Calculate final price with coupon
  const getFinalPrice = () => {
    const hostingPrice = breakdown?.total || 0;
    const selectedDomainInfo = getSelectedDomainInfo();
    const domainPrice = (domainOption === 'purchase' && selectedDomainInfo?.price_registration) ? selectedDomainInfo.price_registration : 0;
    const totalPrice = hostingPrice + domainPrice;
    
    if (!appliedCoupon) return totalPrice;
    
    // The discount_amount in appliedCoupon is already calculated correctly
    // considering applies_to_domain (done in validateCoupon)
    return Math.max(0, totalPrice - appliedCoupon.discount_amount);
  };

  // Calculate monthly price (hosting only, recurring after first payment)
  // Note: Domain is a one-time yearly fee, not monthly
  const getMonthlyPrice = () => {
    const hostingPrice = breakdown?.total || 0;
    
    // For recurring monthly payments, coupon typically only applies to first month
    // or is a percentage discount that continues
    // For simplicity, we show full hosting price for "Dann monatlich"
    // since most coupons are one-time or first-month discounts
    return hostingPrice;
  };

  // Manual domain search function (no auto-search to reduce API calls)
  const handleDomainSearch = async () => {
    if (!purchaseDomain || purchaseDomain.length < 4) {
      toast.error('Bitte gib eine gültige Domain ein (z.B. meinefirma.de)');
      return;
    }

    // Extract SLD and TLD from full domain
    const domainLower = purchaseDomain.toLowerCase().trim();
    const dotIndex = domainLower.lastIndexOf('.');
    
    // Need at least one dot for a valid domain
    if (dotIndex === -1 || dotIndex === 0 || dotIndex === domainLower.length - 1) {
      toast.error('Bitte gib eine Domain mit Endung ein (z.B. meinefirma.de)');
      return;
    }

    setDomainPurchaseCheck(prev => ({ ...prev, loading: true, searchedDomain: domainLower }));

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      
      // Send full domain to backend - it will extract SLD and check alternatives
      const response = await fetch(`${backendUrl}/api/domains/search`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ domain: domainLower })
      });

      if (response.ok) {
        const data = await response.json();
        setDomainPurchaseCheck({
          loading: false,
          checked: true,
          searchedDomain: domainLower,
          results: data.results || []
        });
        
        // Auto-select the searched domain if available
        const searchedResult = data.results.find(r => r.domain === domainLower && r.available);
        if (searchedResult) {
          setFormData(prev => ({ ...prev, domain: searchedResult.domain }));
        }
      } else {
        toast.error('Domain-Suche fehlgeschlagen. Bitte versuche es erneut.');
        setDomainPurchaseCheck(prev => ({ ...prev, loading: false }));
      }
    } catch (error) {
      console.error('Domain check error:', error);
      toast.error('Verbindungsfehler. Bitte versuche es erneut.');
      setDomainPurchaseCheck(prev => ({ ...prev, loading: false }));
    }
  };

  // Reset domain search when switching options
  useEffect(() => {
    if (domainOption !== 'purchase') {
      setDomainPurchaseCheck({ loading: false, checked: false, searchedDomain: '', results: [] });
    }
  }, [domainOption]);

  const progress = {
    konfiguration: 50,
    domain: 100
  }[activeTab] || 0;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Reset domain check wenn Domain geändert wird
    if (name === 'domain') {
      setDomainCheck({
        loading: false,
        checked: false,
        isValid: false,
        message: '',
        instructions: null
      });
    }
  };

  const checkDomainCname = async () => {
    if (!formData.domain) {
      toast.error('Bitte gib deine Domain ein.');
      return;
    }

    setDomainCheck(prev => ({ ...prev, loading: true }));

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${backendUrl}/api/dns/check-cname`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ domain: formData.domain })
      });

      if (!response.ok) throw new Error('CNAME-Check fehlgeschlagen');

      const data = await response.json();
      
      setDomainCheck({
        loading: false,
        checked: true,
        isValid: data.is_valid,
        message: data.message,
        instructions: data.instructions
      });

      if (data.is_valid) {
        toast.success('Domain ist korrekt konfiguriert!');
      } else {
        toast.error('CNAME nicht korrekt gesetzt. Siehe Anleitung unten.');
      }
    } catch (error) {
      console.error('Domain check error:', error);
      toast.error('CNAME-Check fehlgeschlagen. Bitte versuche es erneut.');
      setDomainCheck(prev => ({ ...prev, loading: false }));
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('In die Zwischenablage kopiert!');
  };

  // Check subdomain availability
  const checkSubdomainAvailability = async (value) => {
    if (!value || value.length < 3) {
      setSubdomainCheck({
        loading: false,
        checked: false,
        available: false,
        message: value ? 'Mindestens 3 Zeichen erforderlich' : '',
        fullDomain: ''
      });
      return;
    }

    setSubdomainCheck(prev => ({ ...prev, loading: true }));

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${backendUrl}/api/subdomain/check`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ subdomain: value })
      });

      if (!response.ok) throw new Error('Verfügbarkeitsprüfung fehlgeschlagen');

      const data = await response.json();
      
      setSubdomainCheck({
        loading: false,
        checked: true,
        available: data.available,
        message: data.message,
        fullDomain: data.full_domain
      });

      // Auto-set domain when available
      if (data.available) {
        setFormData(prev => ({ ...prev, domain: data.full_domain }));
      }
    } catch (error) {
      console.error('Subdomain check error:', error);
      setSubdomainCheck(prev => ({ 
        ...prev, 
        loading: false,
        message: 'Prüfung fehlgeschlagen. Bitte erneut versuchen.'
      }));
    }
  };

  // Debounced subdomain check
  useEffect(() => {
    if (domainOption !== 'subdomain') return;
    
    const timer = setTimeout(() => {
      if (subdomain) {
        checkSubdomainAvailability(subdomain);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [subdomain, domainOption]);

  const canProceedFromDomain = () => {
    if (domainOption === 'byod') {
      return formData.domain && domainCheck.checked && domainCheck.isValid;
    }
    if (domainOption === 'subdomain') {
      return subdomainCheck.checked && subdomainCheck.available;
    }
    if (domainOption === 'purchase') {
      // Check if a domain is selected (via formData.domain) and available
      if (!formData.domain) return false;
      
      // Find if selected domain is available
      let domainAvailable = false;
      for (let i = 0; i < domainPurchaseCheck.results.length; i++) {
        const r = domainPurchaseCheck.results[i];
        if (r.domain === formData.domain && r.available) {
          domainAvailable = true;
          break;
        }
      }
      if (!domainAvailable) return false;
      
      // If using subdomain, make sure subdomain is entered (min 2 chars)
      if (domainUsage.useSubdomain && (!domainUsage.subdomain || domainUsage.subdomain.length < 2)) {
        return false;
      }
      
      return true;
    }
    return false;
  };

  // Get selected domain purchase info
  const getSelectedDomainInfo = () => {
    if (domainOption !== 'purchase' || !formData.domain) return null;
    for (let i = 0; i < domainPurchaseCheck.results.length; i++) {
      const r = domainPurchaseCheck.results[i];
      if (r.domain === formData.domain && r.available) {
        return r;
      }
    }
    return null;
  };

  const canProceedFromDaten = () => {
    const hasRequiredFields = formData.email && 
                              formData.firstName && 
                              formData.lastName &&
                              formData.street && 
                              formData.zipCode && 
                              formData.city;
    
    // Phone is required for domain purchase (WHOIS requirements)
    if (domainOption === 'purchase' && !formData.phone) {
      return false;
    }
    
    // If not authenticated, password is required (min 8 chars)
    if (!isAuthenticated) {
      const hasPassword = formData.password && formData.password.length >= 8;
      return hasRequiredFields && hasPassword;
    }
    
    return hasRequiredFields;
  };

  const handleSubmitOrder = async () => {
    // Final validation
    if (!canProceedFromDomain()) {
      toast.error('Bitte prüfe zuerst deine Domain.');
      setActiveTab('domain');
      return;
    }

    if (!canProceedFromDaten()) {
      toast.error('Bitte fülle alle Pflichtfelder aus.');
      setActiveTab('daten');
      return;
    }

    setLoading(true);

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const originUrl = window.location.origin;

      // Create customer (and user if not authenticated)
      // Stabile Gast-ID (statt Date.now()) → keine Duplikate bei Retry
      const guestId = localStorage.getItem('wolkenhelden_guest_id') || `guest-${crypto.randomUUID ? crypto.randomUUID() : Date.now().toString(36)}`;
      localStorage.setItem('wolkenhelden_guest_id', guestId);
      const customerResponse = await fetch(`${backendUrl}/api/customers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: isAuthenticated && user?.id ? user.id : guestId,
          email: formData.email,
          password: !isAuthenticated ? formData.password : null,
          first_name: formData.firstName,
          last_name: formData.lastName,
          company: formData.company,
          street: formData.street,
          zip_code: formData.zipCode,
          city: formData.city,
          country: formData.country,
          phone: formData.phone || null  // Telefonnummer für Domain-Registrierung
        })
      });

      if (!customerResponse.ok) {
        const errorData = await customerResponse.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Customer creation failed');
      }
      const customer = await customerResponse.json();

      // Create order
      const selectedDomainInfo = getSelectedDomainInfo();
      const orderResponse = await fetch(`${backendUrl}/api/orders`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customer_id: customer.id,
          plan: {
            storage_gb,
            collabora_users,
            antivirus,
            hp_backend,
            custom_domain: formData.domain,
            domain_type: domainOption,
            domain_price: domainOption === 'purchase' ? selectedDomainInfo?.price_registration : null,
            // Domain usage configuration (for purchased domains)
            cloud_subdomain: domainOption === 'purchase' && domainUsage.useSubdomain ? domainUsage.subdomain : null,
            dns_record_type: domainOption === 'purchase' ? domainUsage.recordType : null,
            // Full cloud URL
            cloud_url: domainOption === 'purchase' 
              ? (domainUsage.useSubdomain && domainUsage.subdomain 
                  ? `${domainUsage.subdomain}.${formData.domain}` 
                  : formData.domain)
              : domainOption === 'subdomain'
                ? `${subdomain}.helden.land`
                : formData.domain
          },
          payment_method: formData.paymentMethod,
          coupon_code: appliedCoupon?.code || null  // Include applied coupon
        })
      });

      if (!orderResponse.ok) {
        const errorData = await orderResponse.json().catch(() => null);
        throw new Error(errorData?.detail || 'Bestellung konnte nicht erstellt werden');
      }
      const order = await orderResponse.json();

      // Track E-Commerce cart for analytics (if consent given)
      if (hasConsent(COOKIE_CATEGORIES.analytics)) {
        try {
          // Add the main product (Cloud hosting)
          addEcommerceItem(
            'wolke-' + storage_gb + 'gb',
            'Managed Nextcloud ' + storage_gb + 'GB',
            'Cloud Hosting',
            breakdown.total_monthly,
            1
          );
          
          // Track cart update
          trackEcommerceCartUpdate(breakdown.total_monthly);
          
          // Track checkout event
          trackEvent('Checkout', 'Order Created', order.id, breakdown.total_monthly);
        } catch (e) {
          console.debug('Matomo tracking error:', e);
        }
      }

      // Initiate payment with Stripe or PayPal
      const paymentResponse = await fetch(`${backendUrl}/api/payments/initiate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          order_id: order.id,
          payment_method: formData.paymentMethod,
          origin_url: originUrl
        })
      });

      if (!paymentResponse.ok) {
        const error = await paymentResponse.json();
        throw new Error(error.detail || 'Payment initiation failed');
      }

      const paymentData = await paymentResponse.json();
      
      // Clear saved checkout data on success
      clearCheckoutData();
      
      // Handle free orders (100% coupon)
      if (paymentData.is_free) {
        toast.success('🎉 Deine Bestellung ist kostenlos! Du wirst weitergeleitet...');
      } else {
        toast.success('Du wirst zur Zahlung weitergeleitet...');
      }
      
      // Small delay for toast to show
      setTimeout(() => {
        window.location.href = paymentData.payment_url;
      }, 500);

    } catch (error) {
      console.error('Checkout error:', error);
      toast.error(error.message || 'Bestellung fehlgeschlagen. Bitte versuche es erneut.');
      setLoading(false);
    }
  };

  return (
    <div className="py-16 md:py-24" data-testid="checkout-page">
      <div className="container mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        <div className="mb-8 text-center">
          <h1 className="font-display text-3xl font-semibold sm:text-4xl">Cloud konfigurieren</h1>
          <p className="mt-2 text-muted-foreground">
            Konfiguriere deine Cloud und füge sie zum Warenkorb hinzu.
          </p>
        </div>

        {/* Progress Bar */}
        <div className="mb-8">
          <Progress value={progress} className="h-2" data-testid="checkout-progress" />
          <div className="mt-2 flex justify-between text-xs text-muted-foreground">
            <span className={activeTab === 'konfiguration' ? 'text-primary font-medium' : ''}>1. Konfiguration</span>
            <span className={activeTab === 'domain' ? 'text-primary font-medium' : ''}>2. Domain</span>
            <span className="text-muted-foreground/50">→ Warenkorb</span>
          </div>
        </div>

        <div className="grid gap-8 lg:grid-cols-3">
          {/* Main Content */}
          <div className="lg:col-span-2">
            <Tabs value={activeTab} onValueChange={setActiveTab} data-testid="checkout-tabs">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="konfiguration" data-testid="tab-konfiguration">
                  <Server className="mr-2 h-4 w-4 hidden sm:inline" />
                  Konfiguration
                </TabsTrigger>
                <TabsTrigger value="domain" data-testid="tab-domain">
                  <Globe className="mr-2 h-4 w-4 hidden sm:inline" />
                  Domain
                </TabsTrigger>
              </TabsList>

              {/* Step 2: Domain Setup */}
              <TabsContent value="domain" className="mt-6 space-y-6">
                {/* Domain Options Selection */}
                <Card className="glass-card">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Globe className="h-5 w-5 text-primary" />
                      Wähle deine Domain-Option
                    </CardTitle>
                    <CardDescription>
                      Unter welcher Adresse soll deine Helden-Wolke erreichbar sein?
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Option 1: BYOD (Recommended) */}
                    <button
                      onClick={() => {
                        setDomainOption('byod');
                        setFormData(prev => ({ ...prev, domain: '' }));
                        setDomainCheck({ loading: false, checked: false, isValid: false, message: '', instructions: null });
                      }}
                      className={`w-full rounded-lg border-2 p-4 text-left transition-all ${
                        domainOption === 'byod'
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:border-primary/50'
                      }`}
                      data-testid="domain-option-byod"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
                            <Globe className="h-5 w-5 text-primary" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-medium">Eigene Domain (BYOD)</span>
                              <Badge variant="secondary" className="bg-primary/20 text-primary text-xs">Empfohlen</Badge>
                            </div>
                            <p className="text-sm text-muted-foreground mt-1">
                              Nutze deine eigene Domain oder Subdomain (z.B. cloud.deinefirma.de)
                            </p>
                          </div>
                        </div>
                        {domainOption === 'byod' && (
                          <CheckCircle2 className="h-5 w-5 text-primary mt-2" />
                        )}
                      </div>
                    </button>

                    {/* Option 2: Free Subdomain */}
                    <button
                      onClick={() => {
                        setDomainOption('subdomain');
                        setFormData(prev => ({ ...prev, domain: '' }));
                        setSubdomain('');
                        setSubdomainCheck({ loading: false, checked: false, available: false, message: '', fullDomain: '' });
                      }}
                      className={`w-full rounded-lg border-2 p-4 text-left transition-all ${
                        domainOption === 'subdomain'
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:border-primary/50'
                      }`}
                      data-testid="domain-option-subdomain"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-green-500/10">
                            <Sparkles className="h-5 w-5 text-green-500" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-medium">Kostenlose Subdomain</span>
                              <Badge variant="secondary" className="bg-green-500/20 text-green-600 text-xs">Gratis</Badge>
                            </div>
                            <p className="text-sm text-muted-foreground mt-1">
                              Wähle einen Namen und nutze wunschname.helden.land
                            </p>
                          </div>
                        </div>
                        {domainOption === 'subdomain' && (
                          <CheckCircle2 className="h-5 w-5 text-primary mt-2" />
                        )}
                      </div>
                    </button>

                    {/* Option 3: Purchase Domain */}
                    <button
                      onClick={() => {
                        setDomainOption('purchase');
                        setFormData(prev => ({ ...prev, domain: '' }));
                        setPurchaseDomain('');
                        setDomainPurchaseCheck({ loading: false, checked: false, searchedDomain: '', results: [] });
                      }}
                      className={`w-full rounded-lg border-2 p-4 text-left transition-all ${
                        domainOption === 'purchase'
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:border-primary/50'
                      }`}
                      data-testid="domain-option-purchase"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-500/10">
                            <CreditCard className="h-5 w-5 text-amber-500" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-medium">Domain kaufen</span>
                              <Badge variant="secondary" className="bg-amber-500/20 text-amber-600 text-xs">Neu</Badge>
                            </div>
                            <p className="text-sm text-muted-foreground mt-1">
                              Registriere eine neue Domain direkt bei uns (.de, .com, .eu, ...)
                            </p>
                          </div>
                        </div>
                        {domainOption === 'purchase' && (
                          <CheckCircle2 className="h-5 w-5 text-primary mt-2" />
                        )}
                      </div>
                    </button>
                  </CardContent>
                </Card>

                {/* BYOD Configuration */}
                {domainOption === 'byod' && (
                  <>
                    <Card className="glass-card border-primary/20 bg-primary/5">
                      <CardHeader className="pb-3">
                        <div className="flex items-center gap-2">
                          <HelpCircle className="h-5 w-5 text-primary" />
                          <CardTitle className="text-lg">So funktioniert BYOD</CardTitle>
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <ol className="text-sm text-muted-foreground space-y-2 ml-4 list-decimal">
                          <li>Wähle eine Domain/Subdomain (z.B. <code className="bg-muted px-1 rounded">cloud.deinefirma.de</code>)</li>
                          <li>Erstelle einen <strong>CNAME-Eintrag</strong> bei deinem DNS-Provider</li>
                          <li>Richte ihn auf <code className="bg-muted px-1.5 py-0.5 rounded font-mono">cloud.wolkenhelden.de</code></li>
                          <li>Wir prüfen die Konfiguration und richten deine Cloud ein</li>
                        </ol>
                        <div className="grid gap-2 sm:grid-cols-2 mt-3">
                          <div className="flex items-center gap-2 text-sm">
                            <CheckCircle2 className="h-4 w-4 text-green-500" />
                            <span>Deine Marke & URL</span>
                          </div>
                          <div className="flex items-center gap-2 text-sm">
                            <CheckCircle2 className="h-4 w-4 text-green-500" />
                            <span>Volle Kontrolle</span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    <Card className="glass-card">
                      <CardHeader>
                        <CardTitle>Deine Domain</CardTitle>
                        <CardDescription>
                          Gib die Domain oder Subdomain ein, unter der deine Cloud erreichbar sein soll.
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="space-y-2">
                          <Label htmlFor="domain">Domain / Subdomain *</Label>
                          <div className="flex gap-2">
                            <Input
                              id="domain"
                              name="domain"
                              data-testid="input-domain"
                              value={formData.domain}
                              onChange={handleInputChange}
                              placeholder="cloud.deinefirma.de"
                              className="flex-1"
                            />
                            <Button
                              type="button"
                              onClick={checkDomainCname}
                              disabled={domainCheck.loading || !formData.domain}
                              data-testid="button-check-domain"
                            >
                              {domainCheck.loading ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                'Prüfen'
                              )}
                            </Button>
                          </div>
                        </div>

                        {domainCheck.checked && (
                          <div className={`rounded-lg p-4 ${
                            domainCheck.isValid 
                              ? 'bg-green-500/10 border border-green-500/30' 
                              : 'bg-amber-500/10 border border-amber-500/30'
                          }`}>
                            <div className="flex items-start gap-2">
                              {domainCheck.isValid ? (
                                <CheckCircle2 className="h-5 w-5 text-green-500 mt-0.5" />
                              ) : (
                                <AlertCircle className="h-5 w-5 text-amber-500 mt-0.5" />
                              )}
                              <div className="space-y-2 flex-1">
                                <p className={`font-medium ${domainCheck.isValid ? 'text-green-600 dark:text-green-400' : 'text-amber-600 dark:text-amber-400'}`}>
                                  {domainCheck.isValid ? 'Domain ist bereit!' : 'CNAME noch nicht konfiguriert'}
                                </p>
                                <p className="text-sm text-muted-foreground">
                                  {domainCheck.message}
                                </p>
                              </div>
                            </div>
                          </div>
                        )}

                        {domainCheck.checked && !domainCheck.isValid && domainCheck.instructions && (
                          <Card className="border-amber-500/30 bg-amber-500/5">
                            <CardHeader className="pb-2">
                              <CardTitle className="text-base flex items-center gap-2">
                                <HelpCircle className="h-4 w-4" />
                                So richtest du den CNAME ein:
                              </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-3 text-sm">
                              <div className="grid gap-2">
                                <div className="flex justify-between items-center p-2 bg-background rounded">
                                  <span className="text-muted-foreground">Host/Name:</span>
                                  <div className="flex items-center gap-2">
                                    <code className="font-mono">{domainCheck.instructions.host}</code>
                                    <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => copyToClipboard(domainCheck.instructions.host)}>
                                      <Copy className="h-3 w-3" />
                                    </Button>
                                  </div>
                                </div>
                                <div className="flex justify-between items-center p-2 bg-background rounded">
                                  <span className="text-muted-foreground">Typ:</span>
                                  <code className="font-mono">{domainCheck.instructions.type}</code>
                                </div>
                                <div className="flex justify-between items-center p-2 bg-background rounded">
                                  <span className="text-muted-foreground">Ziel/Value:</span>
                                  <div className="flex items-center gap-2">
                                    <code className="font-mono">{domainCheck.instructions.target}</code>
                                    <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => copyToClipboard(domainCheck.instructions.target)}>
                                      <Copy className="h-3 w-3" />
                                    </Button>
                                  </div>
                                </div>
                              </div>
                              {domainCheck.instructions.note && (
                                <p className="text-amber-600 dark:text-amber-400 text-xs font-medium">
                                  💡 {domainCheck.instructions.note}
                                </p>
                              )}
                              <p className="text-muted-foreground text-xs">
                                Nach dem Setzen des CNAME kann es bis zu 24 Stunden dauern, bis die Änderung aktiv ist.
                              </p>
                            </CardContent>
                          </Card>
                        )}
                      </CardContent>
                    </Card>
                  </>
                )}

                {/* Subdomain Configuration */}
                {domainOption === 'subdomain' && (
                  <Card className="glass-card">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Sparkles className="h-5 w-5 text-green-500" />
                        Deine kostenlose Subdomain
                      </CardTitle>
                      <CardDescription>
                        Wähle einen Namen für deine Helden-Wolke. Er muss zwischen 3 und 32 Zeichen lang sein.
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="subdomain">Dein Wunschname *</Label>
                        <div className="flex items-center gap-2">
                          <Input
                            id="subdomain"
                            data-testid="input-subdomain"
                            value={subdomain}
                            onChange={(e) => setSubdomain(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                            placeholder="meinefirma"
                            className="flex-1"
                            maxLength={32}
                          />
                          <span className="text-muted-foreground font-medium">.helden.land</span>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Nur Kleinbuchstaben, Zahlen und Bindestriche erlaubt.
                        </p>
                      </div>

                      {/* Live availability check */}
                      {subdomain && (
                        <div className={`rounded-lg p-4 ${
                          subdomainCheck.loading 
                            ? 'bg-muted/50 border border-muted'
                            : subdomainCheck.available 
                              ? 'bg-green-500/10 border border-green-500/30' 
                              : subdomainCheck.checked 
                                ? 'bg-red-500/10 border border-red-500/30'
                                : 'bg-muted/50 border border-muted'
                        }`}>
                          <div className="flex items-center gap-2">
                            {subdomainCheck.loading ? (
                              <>
                                <Loader2 className="h-5 w-5 text-muted-foreground animate-spin" />
                                <span className="text-muted-foreground">Prüfe Verfügbarkeit...</span>
                              </>
                            ) : subdomainCheck.available ? (
                              <>
                                <CheckCircle2 className="h-5 w-5 text-green-500" />
                                <span className="text-green-600 dark:text-green-400 font-medium">{subdomainCheck.message}</span>
                              </>
                            ) : subdomainCheck.checked ? (
                              <>
                                <XCircle className="h-5 w-5 text-red-500" />
                                <span className="text-red-600 dark:text-red-400">{subdomainCheck.message}</span>
                              </>
                            ) : (
                              <>
                                <AlertCircle className="h-5 w-5 text-muted-foreground" />
                                <span className="text-muted-foreground">{subdomainCheck.message}</span>
                              </>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Preview of full domain */}
                      {subdomainCheck.available && subdomainCheck.fullDomain && (
                        <div className="rounded-lg bg-primary/5 border border-primary/20 p-4">
                          <p className="text-sm text-muted-foreground mb-1">Deine Cloud wird erreichbar sein unter:</p>
                          <p className="font-mono text-lg font-medium text-primary">
                            https://{subdomainCheck.fullDomain}
                          </p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* Domain Purchase Configuration */}
                {domainOption === 'purchase' && (
                  <Card className="glass-card">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <CreditCard className="h-5 w-5 text-amber-500" />
                        Domain suchen & registrieren
                      </CardTitle>
                      <CardDescription>
                        Gib deine Wunschdomain ein (z.B. meinefirma.de). Wir zeigen dir auch Alternativen.
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="purchaseDomain">Deine Wunschdomain *</Label>
                        <div className="flex gap-2">
                          <Input
                            id="purchaseDomain"
                            data-testid="input-purchase-domain"
                            value={purchaseDomain}
                            onChange={(e) => {
                              // Allow dots for full domain entry
                              const val = e.target.value.toLowerCase().replace(/[^a-z0-9.-]/g, '');
                              setPurchaseDomain(val);
                              // Clear selection when input changes
                              setFormData(prev => ({ ...prev, domain: '' }));
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                handleDomainSearch();
                              }
                            }}
                            placeholder="meinefirma.de"
                            maxLength={67}
                            className="flex-1"
                          />
                          <Button
                            onClick={handleDomainSearch}
                            disabled={domainPurchaseCheck.loading || !purchaseDomain || purchaseDomain.length < 4}
                            data-testid="button-search-domain"
                          >
                            {domainPurchaseCheck.loading ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <>
                                <Globe className="mr-2 h-4 w-4" />
                                Prüfen
                              </>
                            )}
                          </Button>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Gib die komplette Domain mit Endung ein (z.B. meinefirma.de, example.com)
                        </p>
                      </div>

                      {/* Domain Search Results */}
                      {domainPurchaseCheck.checked && !domainPurchaseCheck.loading && domainPurchaseCheck.results.length > 0 && (
                        <div className="space-y-3">
                          <Label>Verfügbarkeit & Alternativen</Label>
                          <DomainResultsList 
                            results={domainPurchaseCheck.results}
                            searchedDomain={domainPurchaseCheck.searchedDomain}
                            selectedDomain={formData.domain}
                            onSelect={(r) => {
                              setFormData(prev => ({ ...prev, domain: r.domain }));
                              // Eingabefeld an die gewählte Domain/TLD angleichen (sonst Mismatch im Warenkorb)
                              setPurchaseDomain(r.domain);
                            }}
                          />
                          
                          {/* Info about pricing - show if demo mode */}
                          <DemoModeNotice results={domainPurchaseCheck.results} />
                        </div>
                      )}

                      {/* Selected domain preview */}
                      {formData.domain && getSelectedDomainInfo() && (
                        <SelectedDomainPreviewNew
                          selectedDomain={formData.domain}
                          domainInfo={getSelectedDomainInfo()}
                        />
                      )}
                      
                      {/* Domain Usage Configuration - only show when domain is selected */}
                      {formData.domain && domainOption === 'purchase' && (
                        <Card className="border-primary/30 bg-primary/5">
                          <CardHeader className="pb-3">
                            <CardTitle className="text-base flex items-center gap-2">
                              <Globe className="h-4 w-4 text-primary" />
                              Wie möchtest du deine Cloud erreichen?
                            </CardTitle>
                            <CardDescription>
                              Wir richten alles automatisch für dich ein.
                            </CardDescription>
                          </CardHeader>
                          <CardContent className="space-y-4">
                            {/* Option 1: Main Domain */}
                            <div
                              className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                                !domainUsage.useSubdomain
                                  ? 'border-primary bg-primary/10'
                                  : 'border-border hover:border-primary/50'
                              }`}
                              onClick={() => setDomainUsage({ useSubdomain: false, subdomain: '', recordType: 'ALIAS' })}
                            >
                              <div className="flex items-start gap-3">
                                <div className={`mt-0.5 h-4 w-4 rounded-full border-2 flex items-center justify-center ${
                                  !domainUsage.useSubdomain ? 'border-primary' : 'border-muted-foreground'
                                }`}>
                                  {!domainUsage.useSubdomain && <div className="h-2 w-2 rounded-full bg-primary" />}
                                </div>
                                <div className="flex-1">
                                  <p className="font-medium">Direkt unter {formData.domain}</p>
                                  <p className="text-sm text-muted-foreground mt-1">
                                    Deine Cloud ist direkt unter <span className="font-mono text-primary">{formData.domain}</span> erreichbar.
                                  </p>
                                </div>
                              </div>
                            </div>
                            
                            {/* Option 2: Subdomain */}
                            <div
                              className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                                domainUsage.useSubdomain
                                  ? 'border-primary bg-primary/10'
                                  : 'border-border hover:border-primary/50'
                              }`}
                              onClick={() => setDomainUsage(prev => ({ ...prev, useSubdomain: true, recordType: 'CNAME' }))}
                            >
                              <div className="flex items-start gap-3">
                                <div className={`mt-0.5 h-4 w-4 rounded-full border-2 flex items-center justify-center ${
                                  domainUsage.useSubdomain ? 'border-primary' : 'border-muted-foreground'
                                }`}>
                                  {domainUsage.useSubdomain && <div className="h-2 w-2 rounded-full bg-primary" />}
                                </div>
                                <div className="flex-1">
                                  <p className="font-medium">Unter einer Subdomain</p>
                                  <p className="text-sm text-muted-foreground mt-1">
                                    Deine Cloud ist unter einer Subdomain erreichbar (z.B. cloud.{formData.domain}).
                                  </p>
                                </div>
                              </div>
                            </div>
                            
                            {/* Subdomain input - only show when subdomain option selected */}
                            {domainUsage.useSubdomain && (
                              <div className="space-y-2 pt-2">
                                <Label htmlFor="subdomain-input">Subdomain wählen *</Label>
                                <div className="flex items-center gap-2">
                                  <Input
                                    id="subdomain-input"
                                    data-testid="input-cloud-subdomain"
                                    value={domainUsage.subdomain}
                                    onChange={(e) => {
                                      const val = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '');
                                      setDomainUsage(prev => ({ ...prev, subdomain: val }));
                                    }}
                                    placeholder="cloud"
                                    maxLength={32}
                                    className="max-w-[150px]"
                                  />
                                  <span className="text-muted-foreground">.{formData.domain}</span>
                                </div>
                                <p className="text-xs text-muted-foreground">
                                  Beliebte Subdomains: cloud, nextcloud, files, office
                                </p>
                              </div>
                            )}
                            
                            {/* Final URL Preview */}
                            <div className="mt-4 p-3 rounded-lg bg-background border">
                              <p className="text-sm text-muted-foreground mb-1">Deine Cloud-Adresse:</p>
                              <p className="font-mono text-primary font-medium">
                                https://{domainUsage.useSubdomain && domainUsage.subdomain ? `${domainUsage.subdomain}.` : ''}{formData.domain}
                              </p>
                            </div>
                          </CardContent>
                        </Card>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* Navigation */}
                <div className="flex gap-4 pt-4">
                  <Button
                    variant="outline"
                    onClick={() => setActiveTab('konfiguration')}
                    data-testid="button-back-to-config"
                  >
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Zurück
                  </Button>
                  <Button
                    onClick={addCloudToCart}
                    className="flex-1"
                    disabled={!canProceedFromDomain()}
                    data-testid="button-add-cloud-to-cart"
                  >
                    <ShoppingCart className="mr-2 h-4 w-4" />
                    Zum Warenkorb hinzufügen
                  </Button>
                </div>
              </TabsContent>

              {/* Step 1: Konfiguration (Cloud Options) */}
              <TabsContent value="konfiguration" className="mt-6 space-y-6">
                <Card className="glass-card">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Server className="h-5 w-5 text-primary" />
                      Cloud-Konfiguration
                    </CardTitle>
                    <CardDescription>
                      Passe deinen Helden-Speicher und Zusatzfunktionen an.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {/* Storage Slider */}
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="space-y-1">
                          <Label className="flex items-center gap-2">
                            <Sparkles className="h-4 w-4 text-primary" />
                            Helden-Speicher
                          </Label>
                          <p className="text-xs text-muted-foreground">250 GB inklusive</p>
                        </div>
                        <Badge variant="secondary" className="text-lg px-3">{storage_gb} GB</Badge>
                      </div>
                      <Slider
                        min={250}
                        max={2000}
                        step={50}
                        value={[storage_gb]}
                        onValueChange={([value]) => setStorageGb(value)}
                        data-testid="config-storage-slider"
                      />
                      <p className="text-xs text-muted-foreground">
                        Zusätzliche 50 GB: {prices?.storage_extra_50gb ? `${prices.storage_extra_50gb.toFixed(2).replace('.', ',')} €/Monat` : '...'}
                      </p>
                    </div>

                    <Separator />

                    {/* Collabora Users */}
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="space-y-1">
                          <Label className="flex items-center gap-2">
                            <Zap className="h-4 w-4 text-primary" />
                            Collabora Office
                          </Label>
                          <p className="text-xs text-muted-foreground">Gemeinsam Dokumente bearbeiten</p>
                        </div>
                        <Badge variant="secondary" className="text-lg px-3">{collabora_users} Helden</Badge>
                      </div>
                      <Slider
                        min={0}
                        max={50}
                        step={1}
                        value={[collabora_users]}
                        onValueChange={([value]) => setCollaboraUsers(value)}
                        data-testid="config-collabora-slider"
                      />
                      <p className="text-xs text-muted-foreground">
                        {prices?.collabora_per_user ? `${prices.collabora_per_user.toFixed(2).replace('.', ',')} €/Monat pro Held` : '...'}
                      </p>
                    </div>

                    <Separator />

                    {/* Toggles */}
                    <div className="space-y-4">
                      <div className="flex items-center justify-between rounded-lg border p-4">
                        <div className="space-y-0.5">
                          <Label className="text-sm flex items-center gap-2">
                            <Rocket className="h-4 w-4 text-primary" />
                            Turbo-Antrieb (High Performance)
                          </Label>
                          <p className="text-xs text-muted-foreground">Für Nextcloud Talk & Video-Konferenzen</p>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-sm font-medium">{prices?.hp_backend ? `${prices.hp_backend.toFixed(2).replace('.', ',')} €` : '...'}</span>
                          <Switch
                            checked={hp_backend}
                            onCheckedChange={setHpBackend}
                            data-testid="config-hp-switch"
                          />
                        </div>
                      </div>

                      <div className="flex items-center justify-between rounded-lg border p-4">
                        <div className="space-y-0.5">
                          <Label className="text-sm flex items-center gap-2">
                            <Shield className="h-4 w-4 text-primary" />
                            Helden-Schutzschild (Antivirus)
                          </Label>
                          <p className="text-xs text-muted-foreground">Automatischer Virenscan für alle Dateien</p>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-sm font-medium">{prices?.antivirus ? `${prices.antivirus.toFixed(2).replace('.', ',')} €` : '...'}</span>
                          <Switch
                            checked={antivirus}
                            onCheckedChange={setAntivirus}
                            data-testid="config-antivirus-switch"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Navigation */}
                    <div className="flex justify-end pt-4">
                      <Button
                        onClick={() => setActiveTab('domain')}
                        data-testid="button-next-to-domain"
                      >
                        Weiter zur Domain
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Step 3: Daten */}
              <TabsContent value="daten" className="mt-6">
                <Card className="glass-card">
                  <CardHeader>
                    <CardTitle>Deine Angaben</CardTitle>
                    <CardDescription>Für die Rechnung und dein Benutzerkonto</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    
                    {/* Info-Box wenn eingeloggt */}
                    {isAuthenticated && existingCustomer && (
                      <div className="rounded-lg bg-primary/10 border border-primary/20 p-4 mb-4">
                        <div className="flex items-center gap-2 text-primary">
                          <CheckCircle2 className="h-5 w-5" />
                          <span className="font-medium">Du bist angemeldet</span>
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">
                          Deine Rechnungsdaten wurden aus deinem Konto geladen. Du kannst sie hier bei Bedarf anpassen.
                        </p>
                      </div>
                    )}
                    
                    {/* Loading indicator for customer data */}
                    {loadingCustomer && (
                      <div className="flex items-center justify-center py-4">
                        <Loader2 className="h-6 w-6 animate-spin text-primary" />
                        <span className="ml-2 text-muted-foreground">Lade deine Daten...</span>
                      </div>
                    )}
                    
                    <div className="space-y-2">
                      <Label htmlFor="company">Firma (optional)</Label>
                      <Input
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleInputChange}
                        placeholder="Muster GmbH"
                        data-testid="input-company"
                      />
                    </div>

                    <div className="grid gap-4 md:grid-cols-2">
                      <div className="space-y-2">
                        <Label htmlFor="firstName">Vorname *</Label>
                        <Input
                          id="firstName"
                          name="firstName"
                          value={formData.firstName}
                          onChange={handleInputChange}
                          required
                          data-testid="input-first-name"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="lastName">Nachname *</Label>
                        <Input
                          id="lastName"
                          name="lastName"
                          value={formData.lastName}
                          onChange={handleInputChange}
                          required
                          data-testid="input-last-name"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="email">E-Mail *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="deinname@firma.de"
                        disabled={isAuthenticated}  // Nicht änderbar wenn eingeloggt
                        data-testid="input-email"
                      />
                      {isAuthenticated && (
                        <p className="text-xs text-muted-foreground">
                          E-Mail kann nicht geändert werden (mit diesem Konto verknüpft)
                        </p>
                      )}
                    </div>
                    
                    {/* Passwort-Bereich nur wenn NICHT eingeloggt */}
                    {!isAuthenticated && (
                      <div className="rounded-lg border p-4 space-y-4 bg-muted/30">
                        <div className="flex items-center gap-2">
                          <Shield className="h-4 w-4 text-primary" />
                          <span className="font-medium">Benutzerkonto erstellen</span>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          Nach der Bestellung kannst du dich mit E-Mail und Passwort anmelden, um deine Cloud zu verwalten.
                        </p>
                        
                        <div className="space-y-2">
                          <Label htmlFor="password">Passwort *</Label>
                          <Input
                            id="password"
                            name="password"
                            type="password"
                            value={formData.password}
                            onChange={handleInputChange}
                            placeholder="Mindestens 8 Zeichen"
                            minLength={8}
                            required
                            data-testid="input-password"
                          />
                          <p className="text-xs text-muted-foreground">
                            Mindestens 8 Zeichen für ein sicheres Passwort
                          </p>
                        </div>
                      </div>
                    )}

                    <div className="space-y-2">
                      <Label htmlFor="street">Straße & Hausnummer *</Label>
                      <Input
                        id="street"
                        name="street"
                        required
                        value={formData.street}
                        onChange={handleInputChange}
                        data-testid="input-street"
                      />
                    </div>

                    <div className="grid gap-4 md:grid-cols-3">
                      <div className="space-y-2">
                        <Label htmlFor="zipCode">PLZ *</Label>
                        <Input
                          id="zipCode"
                          name="zipCode"
                          required
                          value={formData.zipCode}
                          onChange={handleInputChange}
                          data-testid="input-zip"
                        />
                      </div>
                      <div className="space-y-2 md:col-span-2">
                        <Label htmlFor="city">Stadt *</Label>
                        <Input
                          id="city"
                          name="city"
                          required
                          value={formData.city}
                          onChange={handleInputChange}
                          data-testid="input-city"
                        />
                      </div>
                    </div>
                    
                    {/* Telefonnummer - erforderlich für Domain-Registrierung */}
                    <div className="space-y-2">
                      <Label htmlFor="phone">
                        Telefonnummer {domainOption === 'purchase' ? '*' : '(optional)'}
                      </Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        required={domainOption === 'purchase'}
                        value={formData.phone || ''}
                        onChange={handleInputChange}
                        placeholder="+49 123 4567890"
                        data-testid="input-phone"
                      />
                      {domainOption === 'purchase' && (
                        <p className="text-xs text-muted-foreground">
                          Erforderlich für die Domain-Registrierung (WHOIS-Daten)
                        </p>
                      )}
                    </div>

                    <div className="flex gap-4 pt-4">
                      <Button
                        variant="outline"
                        onClick={() => setActiveTab('domain')}
                        data-testid="button-back-to-domain"
                      >
                        <ArrowLeft className="mr-2 h-4 w-4" />
                        Zurück
                      </Button>
                      <Button
                        onClick={() => setActiveTab('zahlung')}
                        className="flex-1"
                        disabled={!canProceedFromDaten()}
                        data-testid="button-next-to-zahlung"
                      >
                        Weiter zur Zahlung
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Step 3: Zahlung */}
              <TabsContent value="zahlung" className="mt-6">
                <Card className="glass-card">
                  <CardHeader>
                    <CardTitle>Zahlungsmethode</CardTitle>
                    <CardDescription>
                      Wähle deine bevorzugte Zahlungsart. Du richtest eine Einzugsermächtigung für monatliche Abbuchungen ein.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Info about recurring payments */}
                    <div className="rounded-lg bg-primary/5 border border-primary/20 p-4 text-sm">
                      <div className="flex items-start gap-2">
                        <Shield className="h-5 w-5 text-primary mt-0.5" />
                        <div>
                          <p className="font-medium">Monatliche Abbuchung</p>
                          <p className="text-muted-foreground mt-1">
                            Dein Abo wird jeden Monat automatisch verlängert. Du kannst jederzeit im Portal kündigen.
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <button
                        onClick={() => setFormData(prev => ({ ...prev, paymentMethod: 'stripe' }))}
                        className={`w-full rounded-lg border-2 p-4 text-left transition-all ${
                          formData.paymentMethod === 'stripe'
                            ? 'border-primary bg-primary/5'
                            : 'border-border hover:border-primary/50'
                        }`}
                        data-testid="payment-method-stripe"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#635BFF]/10">
                              <CreditCard className="h-5 w-5 text-[#635BFF]" />
                            </div>
                            <div>
                              <span className="font-medium">Kreditkarte</span>
                              <p className="text-xs text-muted-foreground">Visa, Mastercard, American Express</p>
                            </div>
                          </div>
                          {formData.paymentMethod === 'stripe' && (
                            <CheckCircle2 className="h-5 w-5 text-primary" />
                          )}
                        </div>
                      </button>

                      <button
                        onClick={() => setFormData(prev => ({ ...prev, paymentMethod: 'paypal' }))}
                        className={`w-full rounded-lg border-2 p-4 text-left transition-all ${
                          formData.paymentMethod === 'paypal'
                            ? 'border-primary bg-primary/5'
                            : 'border-border hover:border-primary/50'
                        }`}
                        data-testid="payment-method-paypal"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#003087]/10">
                              <span className="text-[#003087] font-bold text-sm">PP</span>
                            </div>
                            <div>
                              <span className="font-medium">PayPal</span>
                              <p className="text-xs text-muted-foreground">Schnell und sicher mit PayPal-Konto</p>
                            </div>
                          </div>
                          {formData.paymentMethod === 'paypal' && (
                            <CheckCircle2 className="h-5 w-5 text-primary" />
                          )}
                        </div>
                      </button>
                    </div>

                    <div className="flex gap-4 pt-4">
                      <Button
                        variant="outline"
                        onClick={() => setActiveTab('daten')}
                        data-testid="button-back-to-daten"
                      >
                        <ArrowLeft className="mr-2 h-4 w-4" />
                        Zurück
                      </Button>
                      <Button
                        onClick={() => setActiveTab('bestaetigung')}
                        className="flex-1"
                        data-testid="button-next-to-confirm"
                      >
                        Weiter zur Bestätigung
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Step 4: Bestätigung */}
              <TabsContent value="bestaetigung" className="mt-6">
                <Card className="glass-card">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Receipt className="h-5 w-5" />
                      Bestellung bestätigen
                    </CardTitle>
                    <CardDescription>
                      Prüfe deine Angaben, gib optional einen Gutschein ein und akzeptiere die AGB
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    
                    {/* Gutschein-Eingabe */}
                    <div className="rounded-lg border border-dashed border-primary/30 bg-primary/5 p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <Tag className="h-4 w-4 text-primary" />
                        <span className="font-medium">Gutscheincode</span>
                      </div>
                      
                      {appliedCoupon ? (
                        <div className="flex items-center justify-between bg-green-50 dark:bg-green-950/30 rounded-lg p-3 border border-green-200 dark:border-green-800">
                          <div className="flex items-center gap-2">
                            <CheckCircle2 className="h-5 w-5 text-green-600" />
                            <div>
                              <p className="font-medium text-green-700 dark:text-green-400">{appliedCoupon.code}</p>
                              <p className="text-sm text-green-600 dark:text-green-500">
                                -{appliedCoupon.discount_amount?.toFixed(2)} € Rabatt
                                {appliedCoupon.applies_to_domain === false && domainOption === 'purchase' && getSelectedDomainInfo()?.price_registration > 0 && (
                                  <span className="text-xs text-amber-600 dark:text-amber-400 block mt-1">
                                    (gilt nicht für Domain-Kosten)
                                  </span>
                                )}
                              </p>
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={removeCoupon}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          >
                            <XCircle className="h-4 w-4" />
                          </Button>
                        </div>
                      ) : (
                        <div className="flex gap-2">
                          <Input
                            placeholder="z.B. HELD20"
                            value={couponCode}
                            onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                            className="uppercase"
                            data-testid="input-coupon-code"
                          />
                          <Button
                            onClick={validateCoupon}
                            disabled={couponLoading || !couponCode.trim()}
                            variant="secondary"
                            data-testid="button-validate-coupon"
                          >
                            {couponLoading ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              'Einlösen'
                            )}
                          </Button>
                        </div>
                      )}
                    </div>
                    
                    {/* Bestellübersicht */}
                    <div className="rounded-lg border p-4 space-y-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Receipt className="h-4 w-4 text-primary" />
                        <span className="font-medium">Bestellübersicht</span>
                      </div>
                      
                      {/* Domain */}
                      <div className="flex justify-between items-start py-2 border-b">
                        <div>
                          <p className="text-sm font-medium flex items-center gap-2">
                            <Globe className="h-3 w-3" />
                            Domain
                          </p>
                          <p className="text-xs text-muted-foreground">{formData.domain || 'Nicht angegeben'}</p>
                          {domainOption === 'purchase' && getSelectedDomainInfo() && (
                            <p className="text-xs text-amber-600 mt-1">Wird bei Bestellung registriert</p>
                          )}
                        </div>
                        <div className="text-right">
                          {domainOption === 'purchase' && getSelectedDomainInfo() ? (
                            <div>
                              <span className="text-sm font-medium">{getSelectedDomainInfo().price_registration?.toFixed(2)} €</span>
                              <span className="text-xs text-muted-foreground">/Jahr</span>
                            </div>
                          ) : domainCheck.isValid ? (
                            <Badge variant="outline" className="text-green-600 border-green-500 text-xs">
                              <CheckCircle2 className="mr-1 h-3 w-3" /> OK
                            </Badge>
                          ) : domainOption === 'subdomain' && subdomainCheck.available ? (
                            <Badge variant="outline" className="text-green-600 border-green-500 text-xs">
                              Gratis
                            </Badge>
                          ) : null}
                        </div>
                      </div>
                      
                      {/* Konfiguration */}
                      <div className="flex justify-between items-start py-2 border-b">
                        <div>
                          <p className="text-sm font-medium flex items-center gap-2">
                            <Server className="h-3 w-3" />
                            Konfiguration
                          </p>
                          <ul className="text-xs text-muted-foreground mt-1 space-y-0.5">
                            <li>{storage_gb} GB Speicher</li>
                            {collabora_users > 0 && <li>{collabora_users} Team-Helden</li>}
                            {hp_backend && <li>Turbo-Antrieb</li>}
                            {antivirus && <li>Antivirus</li>}
                          </ul>
                        </div>
                        <span className="text-sm font-medium">{breakdown?.base_package?.toFixed(2) || '0.00'} €</span>
                      </div>
                      
                      {/* Optionen Aufpreis */}
                      {(breakdown?.collabora > 0 || breakdown?.hp_backend > 0 || breakdown?.antivirus > 0) && (
                        <div className="flex justify-between items-start py-2 border-b">
                          <div>
                            <p className="text-sm font-medium flex items-center gap-2">
                              <Zap className="h-3 w-3" />
                              Optionen
                            </p>
                            <ul className="text-xs text-muted-foreground mt-1 space-y-0.5">
                              {breakdown?.collabora > 0 && <li>Collabora: +{breakdown.collabora?.toFixed(2)} €</li>}
                              {breakdown?.hp_backend > 0 && <li>Turbo-Antrieb: +{breakdown.hp_backend?.toFixed(2)} €</li>}
                              {breakdown?.antivirus > 0 && <li>Antivirus: +{breakdown.antivirus?.toFixed(2)} €</li>}
                            </ul>
                          </div>
                          <span className="text-sm font-medium">
                            +{((breakdown?.collabora || 0) + (breakdown?.hp_backend || 0) + (breakdown?.antivirus || 0)).toFixed(2)} €
                          </span>
                        </div>
                      )}
                      
                      {/* Rechnungsadresse */}
                      <div className="flex justify-between items-start py-2 border-b">
                        <div>
                          <p className="text-sm font-medium flex items-center gap-2">
                            <FileText className="h-3 w-3" />
                            Rechnungsadresse
                          </p>
                          <div className="text-xs text-muted-foreground mt-1">
                            {formData.company && <p>{formData.company}</p>}
                            <p>{formData.firstName} {formData.lastName}</p>
                            <p>{formData.street}, {formData.zipCode} {formData.city}</p>
                          </div>
                        </div>
                      </div>
                      
                      {/* Zahlungsmethode */}
                      <div className="flex justify-between items-start py-2 border-b">
                        <div>
                          <p className="text-sm font-medium flex items-center gap-2">
                            <CreditCard className="h-3 w-3" />
                            Zahlungsmethode
                          </p>
                          <p className="text-xs text-muted-foreground mt-1">
                            {formData.paymentMethod === 'stripe' && 'Kreditkarte (Stripe)'}
                            {formData.paymentMethod === 'paypal' && 'PayPal'}
                          </p>
                        </div>
                      </div>
                      
                      {/* Rabatt wenn vorhanden */}
                      {appliedCoupon && (
                        <div className="flex justify-between items-center py-2 border-b text-green-600">
                          <div className="flex items-center gap-2">
                            <Tag className="h-3 w-3" />
                            <div>
                              <span className="text-sm font-medium">Gutschein: {appliedCoupon.code}</span>
                              {appliedCoupon.applies_to_domain === false && domainOption === 'purchase' && getSelectedDomainInfo()?.price_registration > 0 && (
                                <span className="text-xs text-amber-600 dark:text-amber-400 block">(ohne Domain)</span>
                              )}
                            </div>
                          </div>
                          <span className="text-sm font-medium">-{appliedCoupon.discount_amount?.toFixed(2)} €</span>
                        </div>
                      )}
                      
                      {/* OHNE Domain: Einfache monatliche Anzeige */}
                      {!(domainOption === 'purchase' && getSelectedDomainInfo()) && (
                        <div className="flex justify-between items-center pt-3">
                          <div>
                            <p className="font-bold text-lg">Monatlicher Preis</p>
                            <p className="text-xs text-muted-foreground">inkl. 19% MwSt.</p>
                          </div>
                          <div className="text-right">
                            {appliedCoupon && (
                              <p className="text-sm text-muted-foreground line-through">
                                {breakdown?.total?.toFixed(2)} €
                              </p>
                            )}
                            <p className="font-bold text-2xl text-primary" data-testid="checkout-today-total">
                              {getFinalPrice().toFixed(2)} €
                            </p>
                          </div>
                        </div>
                      )}
                      
                      {/* MIT Domain: Erweiterte Ansicht */}
                      {domainOption === 'purchase' && getSelectedDomainInfo() && (
                        <>
                          {/* Domain-Zeile */}
                          <div className="flex justify-between items-center py-2 border-b">
                            <div className="flex items-center gap-2">
                              <Globe className="h-3 w-3 text-amber-500" />
                              <span className="text-sm">Domain ({formData.domain})</span>
                              <Badge variant="outline" className="text-xs">1 Jahr</Badge>
                            </div>
                            <span className="text-sm font-medium">
                              {getSelectedDomainInfo().price_registration?.toFixed(2)} €
                            </span>
                          </div>
                          
                          {/* Erste Zahlung */}
                          <div className="flex justify-between items-center pt-3 pb-2 bg-primary/5 -mx-4 px-4 rounded-lg mt-2">
                            <div>
                              <p className="font-bold text-lg flex items-center gap-2">
                                <CreditCard className="h-4 w-4 text-primary" />
                                Heute zu zahlen
                              </p>
                              <p className="text-xs text-muted-foreground">Cloud (1 Monat) + Domain (1 Jahr)</p>
                            </div>
                            <p className="font-bold text-2xl text-primary" data-testid="checkout-today-total">
                              {getFinalPrice().toFixed(2)} €
                            </p>
                          </div>
                          
                          {/* Ab nächstem Monat */}
                          <div className="flex justify-between items-center pt-2 text-sm">
                            <span className="text-muted-foreground flex items-center gap-2">
                              <RefreshCw className="h-3 w-3" />
                              Dann monatlich
                            </span>
                            <span className="font-medium">{getMonthlyPrice().toFixed(2)} €</span>
                          </div>
                          
                          {/* Domain-Hinweis */}
                          <p className="text-xs text-muted-foreground mt-2 flex items-start gap-1.5">
                            <Calendar className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                            Domain wird jährlich verlängert (nächste Zahlung in 12 Monaten)
                          </p>
                        </>
                      )}
                    </div>

                    {/* AGB & SLA Checkboxen */}
                    <div className="space-y-4 rounded-lg border p-4 bg-muted/30">
                      <div className="flex items-center gap-2 mb-2">
                        <Scale className="h-4 w-4 text-primary" />
                        <span className="font-medium">Rechtliche Vereinbarungen</span>
                      </div>
                      
                      {/* AGB Checkbox */}
                      <div className="flex items-start gap-3">
                        <Checkbox
                          id="agb"
                          checked={agbAccepted}
                          onCheckedChange={setAgbAccepted}
                          data-testid="checkbox-agb"
                        />
                        <label htmlFor="agb" className="text-sm leading-relaxed cursor-pointer">
                          Ich habe die{' '}
                          <a 
                            href="https://wolkenhelden.de/agb" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-primary underline hover:text-primary/80 inline-flex items-center gap-1"
                          >
                            Allgemeinen Geschäftsbedingungen (AGB)
                            <ExternalLink className="h-3 w-3" />
                          </a>
                          {' '}und die{' '}
                          <a 
                            href="https://wolkenhelden.de/datenschutz" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-primary underline hover:text-primary/80 inline-flex items-center gap-1"
                          >
                            Datenschutzerklärung
                            <ExternalLink className="h-3 w-3" />
                          </a>
                          {' '}gelesen und akzeptiere diese.
                        </label>
                      </div>
                      
                      {/* SLA Checkbox */}
                      <div className="flex items-start gap-3">
                        <Checkbox
                          id="sla"
                          checked={slaAccepted}
                          onCheckedChange={setSlaAccepted}
                          data-testid="checkbox-sla"
                        />
                        <label htmlFor="sla" className="text-sm leading-relaxed cursor-pointer">
                          Ich habe die{' '}
                          <a 
                            href="https://wolkenhelden.de/sla" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-primary underline hover:text-primary/80 inline-flex items-center gap-1"
                          >
                            Service Level Agreement (SLA)
                            <ExternalLink className="h-3 w-3" />
                          </a>
                          {' '}und die{' '}
                          <a 
                            href="https://wolkenhelden.de/avv" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-primary underline hover:text-primary/80 inline-flex items-center gap-1"
                          >
                            Auftragsverarbeitungsvereinbarung (AVV)
                            <ExternalLink className="h-3 w-3" />
                          </a>
                          {' '}gelesen und akzeptiere diese.
                        </label>
                      </div>
                      
                      {/* Hinweis zur monatlichen Abbuchung */}
                      <div className="text-xs text-muted-foreground mt-2 pl-6">
                        Mit der Bestellung stimmst du der monatlichen automatischen Abbuchung von{' '}
                        <span className="font-medium">{getFinalPrice().toFixed(2)} €</span> zu.
                      </div>
                    </div>
                    
                    {/* Warnung wenn nicht akzeptiert */}
                    {(!agbAccepted || !slaAccepted) && (
                      <div className="flex items-center gap-2 text-amber-600 dark:text-amber-500 text-sm">
                        <AlertCircle className="h-4 w-4" />
                        Bitte akzeptiere die AGB und SLA um fortzufahren
                      </div>
                    )}

                    <div className="flex gap-4">
                      <Button
                        variant="outline"
                        onClick={() => setActiveTab('zahlung')}
                        data-testid="button-back-to-zahlung"
                      >
                        <ArrowLeft className="mr-2 h-4 w-4" />
                        Zurück
                      </Button>
                      <Button
                        variant="outline"
                        onClick={addCloudToCart}
                        disabled={loading}
                        className="flex-1"
                        data-testid="button-add-to-cart"
                      >
                        <ShoppingCart className="mr-2 h-4 w-4" />
                        Zum Warenkorb
                      </Button>
                      <Button
                        onClick={handleSubmitOrder}
                        disabled={loading || !agbAccepted || !slaAccepted}
                        className="flex-1 bg-gradient-to-r from-primary to-blue-600 disabled:opacity-50"
                        size="lg"
                        data-testid="button-submit-order"
                      >
                        {loading ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Wird verarbeitet...
                          </>
                        ) : (
                          <>
                            <Rocket className="mr-2 h-4 w-4" />
                            Jetzt bestellen
                          </>
                        )}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>

          {/* Sidebar: Price Summary */}
          <div className="lg:col-span-1">
            <div className="sticky top-20">
              <Card className="glass-card border-primary/20">
                <CardHeader className="pb-3">
                  <CardTitle className="font-display text-lg">Dein Preis</CardTitle>
                  <CardDescription>inkl. 19% MwSt.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {breakdown ? (
                    <>
                      {/* Monatliche Kosten Aufschlüsselung */}
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Grundpaket (250 GB)</span>
                          <span>{breakdown.base_package.toFixed(2)} €</span>
                        </div>
                        
                        {breakdown.extra_storage > 0 && (
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">
                              Extra Speicher (+{breakdown.extra_storage_gb} GB)
                            </span>
                            <span>{breakdown.extra_storage.toFixed(2)} €</span>
                          </div>
                        )}
                        
                        {breakdown.collabora > 0 && (
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">
                              Team-Helden ({breakdown.collabora_users} Nutzer)
                            </span>
                            <span>{breakdown.collabora.toFixed(2)} €</span>
                          </div>
                        )}
                        
                        {breakdown.hp_backend > 0 && (
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">HP Backend</span>
                            <span>{breakdown.hp_backend.toFixed(2)} €</span>
                          </div>
                        )}
                        
                        {breakdown.antivirus > 0 && (
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Antivirus</span>
                            <span>{breakdown.antivirus.toFixed(2)} €</span>
                          </div>
                        )}
                      </div>

                      <Separator />

                      {/* OHNE Domain: Einfache monatliche Anzeige */}
                      {!(domainOption === 'purchase' && getSelectedDomainInfo()) && (
                        <div className="flex justify-between items-center">
                          <span className="font-semibold">Monatlich</span>
                          <span className="text-2xl font-bold text-primary font-display" data-testid="checkout-total-price">
                            {breakdown.total.toFixed(2)} €
                          </span>
                        </div>
                      )}

                      {/* MIT Domain: Erweiterte Ansicht */}
                      {domainOption === 'purchase' && getSelectedDomainInfo() && (
                        <div className="space-y-4">
                          {/* Erste Zahlung heute */}
                          <div className="rounded-lg border-2 border-primary/30 bg-primary/5 p-4">
                            <div className="flex items-center gap-2 text-primary mb-3">
                              <CreditCard className="h-4 w-4" />
                              <span className="font-semibold text-sm">Erste Zahlung heute</span>
                            </div>
                            
                            <div className="space-y-1.5 text-sm mb-3">
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Cloud (1. Monat)</span>
                                <span>{breakdown.total.toFixed(2)} €</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Domain (1 Jahr)</span>
                                <span>{getSelectedDomainInfo().price_registration?.toFixed(2)} €</span>
                              </div>
                              {/* Gutschein-Rabatt anzeigen */}
                              {appliedCoupon && (
                                <div className="flex justify-between text-green-600">
                                  <span>Gutschein ({appliedCoupon.code})</span>
                                  <span>-{appliedCoupon.discount_amount?.toFixed(2)} €</span>
                                </div>
                              )}
                            </div>
                            
                            <div className="flex justify-between items-center pt-2 border-t border-primary/20">
                              <span className="font-bold">Gesamt</span>
                              <span className="text-2xl font-bold text-primary font-display" data-testid="checkout-total-price">
                                {getFinalPrice().toFixed(2)} €
                              </span>
                            </div>
                          </div>
                          
                          {/* Folgemonate */}
                          <div className="flex justify-between items-center text-sm bg-muted/30 rounded-lg p-3">
                            <span className="text-muted-foreground flex items-center gap-2">
                              <RefreshCw className="h-3.5 w-3.5" />
                              Dann monatlich
                            </span>
                            <span className="font-semibold">{getMonthlyPrice().toFixed(2)} €</span>
                          </div>
                          
                          {/* Domain-Hinweis */}
                          <p className="text-xs text-muted-foreground flex items-start gap-1.5">
                            <Globe className="h-3.5 w-3.5 mt-0.5 flex-shrink-0 text-amber-500" />
                            <span>
                              Domain <strong>{formData.domain}</strong> wird jährlich verlängert ({getSelectedDomainInfo().price_registration?.toFixed(2)} €/Jahr)
                            </span>
                          </p>
                        </div>
                      )}

                      <div className="rounded-lg bg-muted/50 p-3 space-y-1">
                        <p className="text-xs font-medium flex items-center gap-1">
                          <Check className="h-3 w-3 text-primary" /> Immer inklusive:
                        </p>
                        <ul className="text-xs text-muted-foreground space-y-0.5">
                          <li>• Hochverfügbarkeit (HA)</li>
                          <li>• S3-Objektspeicher</li>
                          <li>• Automatische Updates</li>
                          <li>• Support per E-Mail</li>
                        </ul>
                      </div>
                    </>
                  ) : (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin text-primary" />
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
